import React, { FC, useState, useEffect, ChangeEvent, useContext } from 'react'
import { useParams } from 'react-router-dom'
import moment from 'moment-timezone'
import { Button } from 'transfix-ui/components/Button'
import { INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import { TIME_INPUT_FORMAT, TimeInput, buildDateTime } from 'transfix-ui/components/TimeInput'
import { ToastState } from 'transfix-ui/components/Toast'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { FacilityDetails } from 'apps/fleet-planner/components/load-details/stops/FacilityDetails'
import { STOP_TYPE, LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { UserContext } from 'apps/fleet-planner/context'
import {
  INVALID_STATUS_MESSAGE,
  useFMSAnalytics,
  FUTURE_DATE_INPUT_MESSAGE,
} from 'apps/fleet-planner/hooks'
import { DRIVER_IN_OUT_FORM_STATE } from 'apps/fleet-planner/hooks/useDriverInOutForm'
import {
  IStop,
  IShipmentStatusUpdate,
  IShipmentInOutUpdate,
  IGetShipmentResponse,
} from 'apps/fleet-planner/types'
import {
  getCurrentStopData,
  getStopNumber,
  IGetNextDriverStatus,
} from 'apps/fleet-planner/utils/driverStatuses'
import { DatePicker } from 'components/forms'
import { capitalize } from 'utils/capitalize'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'
import { formatDateTime } from '../load-details/stops/utils'
import { getInlineMessageProps } from '../modals/CreateLoadOverlay'
import { FullPageOverlay } from '../modals/full-page-overlay/FullPageOverlay'
import TrailerNumberPrompt from './TrailerNumberPrompt'
import {
  checkLateArrival,
  getInitialTime,
  getLabels,
  shouldCheckLateArrival,
  shouldPromptForTrailerNumber,
} from './utils/InOutTimes'

export interface IOutTimesFormProps {
  nextStatusData: IGetNextDriverStatus
  isOpen: boolean
  onClose: () => void
  onClickTrailerPrompt: () => void
  onToastOpen: (newToastState: Omit<ToastState, 'toastOpen'>) => void
  onSubmit: (
    shipmentId: string,
    shipment: IShipmentStatusUpdate
  ) => Promise<IGetShipmentResponse | undefined>
  onEdit: (
    shipmentId: string,
    stopId: string,
    stop: IShipmentInOutUpdate
  ) => Promise<{} | undefined>
  sendLocation: (externalStopId: string) => void
  currentStop?: IStop
  currentStopSequence?: string
  stops: IStop[]
  formState: DRIVER_IN_OUT_FORM_STATE
  shipmentUpdateErrors?: string
  isShipmentUpdating?: boolean
  isTimeZoneError?: boolean
  isExternal: boolean
  status: LOAD_STATE
}

export const InOutTimesForm: FC<IOutTimesFormProps> = ({
  currentStop,
  currentStopSequence,
  formState,
  isExternal,
  isOpen,
  isShipmentUpdating,
  isTimeZoneError,
  nextStatusData,
  onClickTrailerPrompt,
  onClose,
  onEdit,
  onSubmit,
  onToastOpen,
  sendLocation,
  shipmentUpdateErrors,
  stops,
}) => {
  const { timeFormats } = useContext(UserContext)

  const [date, setDate] = useState<string>('')
  const [time, setTime] = useState<string>('')
  const [showTrailerPrompt, setShowTrailerPrompt] = useState<boolean>(false)

  const isDropTrailerNumberFeatureEnabled = useFeatureFlag('dropTrailerNumber')

  const { trackEvent } = useFMSAnalytics()
  const currentStopSequenceNumber = parseInt(currentStopSequence || '1', 10)
  const { nextSequence, nextStatus = LOAD_STATE.Pending } = nextStatusData || {}
  const { id: loadId = '' } = useParams<{ id: string }>()
  const {
    displayTimezone = '',
    endAt,
    externalId: externalStopId = '',
    id: stopId = '',
    startAt,
    stopSequence = 1,
    stopType = '',
  } = currentStop || {}
  const initialTZ = displayTimezone || moment.tz.guess()
  const appointmentDateTime =
    formatDateTime(timeFormats, startAt, endAt, displayTimezone) || DISPLAY.empty

  const sequence =
    formState === DRIVER_IN_OUT_FORM_STATE.statusUpdate
      ? // when updating the form, use the stopSequence of the load
        currentStopSequenceNumber
      : // else, when editing an in/out time, use the stopSequence of the current stop being edited
        stopSequence
  const dateTime = getInitialTime(formState, currentStop)
  const { isMultiDropoff, isMultiPickup } = getCurrentStopData(stops, currentStopSequenceNumber)
  const isMulti = stopType === STOP_TYPE.pickup ? isMultiPickup : isMultiDropoff
  const currentStopNumber = getStopNumber(isMulti, sequence, stops) || ''
  const stopTypeNumber = capitalize(`${stopType}${currentStopNumber}`)

  const { checkType, header, message } = getLabels({
    state: formState,
    isMulti,
    currentStopNumber,
    nextStatus,
    currentStop,
  })

  const submitFormHandler = async () => {
    const timestamp = buildDateTime(date, time, TIME_INPUT_FORMAT.MILITARY_SHORT, initialTZ)
    const isTimeInvalid = !timestamp?.isValid()

    if (isTimeInvalid) {
      onToastOpen({
        toastType: 'alert',
        toastMessage: 'Please enter a valid date and time.',
      })
      return undefined
    }

    if (formState === DRIVER_IN_OUT_FORM_STATE.statusUpdate) {
      const request = {
        currentStopSequence: nextSequence,
        status: nextStatus,
        timestamp: timestamp?.toISOString() || '',
      }
      const response = await onSubmit(loadId, request)

      if (response) {
        onToastOpen({
          toastType: 'default',
          toastMessage: <div className='body-bold'>Status Updated. Dispatcher notified.</div>,
        })
        trackEvent('Driver Led Status', 'DRIVER_LED_STATUS_DRIVERS_STATUS_UPDATED')
        if (isExternal) {
          sendLocation(externalStopId)
        }
        onClose()
        return true
      }
      return undefined
    }
    const request = {
      [formState]: timestamp?.toISOString(),
    }
    const response = await onEdit(loadId, stopId, request)
    if (response) {
      onToastOpen({
        toastType: 'default',
        toastMessage: <div className='body-bold'>Load updated.</div>,
      })
      trackEvent('Driver Led Status', 'DRIVER_LED_STATUS_DRIVERS_IN_OUT_FORM_SUBMIT')
      if (isExternal) {
        sendLocation(externalStopId)
      }
      onClose()
      return true
    }
    return undefined
  }

  const submitFormClickHandler = () => {
    const timestamp = buildDateTime(date, time, TIME_INPUT_FORMAT.MILITARY_SHORT, initialTZ)
    const isTimeInvalid = !timestamp?.isValid()

    if (isTimeInvalid) {
      onToastOpen({
        toastType: 'alert',
        toastMessage: 'Please enter a valid date and time.',
      })
      return null
    }

    const shouldShowPrompt = shouldPromptForTrailerNumber({ stops, nextStatus, isExternal })

    if (isDropTrailerNumberFeatureEnabled && shouldShowPrompt) {
      return setShowTrailerPrompt(true)
    }
    return submitFormHandler()
  }

  // Update date and time values when formState changes.
  useEffect(() => {
    const dateString = dateTime
      ? moment(dateTime || '')
          .tz(initialTZ)
          .format(FORMATS.flatpickrDateTimeFormat)
      : ''
    const timeString = dateTime
      ? moment(dateTime || '')
          .tz(initialTZ)
          .format(TIME_INPUT_FORMAT.MILITARY_SHORT)
      : ''
    if (formState) {
      setDate(dateString)
      setTime(timeString)
    }
  }, [formState])

  // Mixpanel tracking for viewing the In/Out form
  useEffect(() => {
    trackEvent('Driver Led Status', 'DRIVER_LED_STATUS_DRIVERS_IN_OUT_FORM_VIEWED')
  }, [])

  const isShouldCheckLateArrival = shouldCheckLateArrival({
    checkType,
    formState,
    isExternal,
    startAt,
  })

  const shouldShowLateArrivalWarning =
    isShouldCheckLateArrival &&
    checkLateArrival({
      date,
      endAt,
      initialTZ,
      startAt,
      time,
    })

  const timeChangeHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setTime(value)
  }

  const dateChangeHandler = (dates: string[]) => {
    if (!dates[0]) {
      setDate('')
    } else {
      setDate(moment(dates[0]).format(FORMATS.flatpickrDateTimeFormat))
    }
  }

  const { isMobile } = useBreakpointContext()
  const inlineMessageProps = getInlineMessageProps(shipmentUpdateErrors)
  const isStatusStale = shipmentUpdateErrors === INVALID_STATUS_MESSAGE
  const timeZoneMessage = isTimeZoneError ? FUTURE_DATE_INPUT_MESSAGE : ''

  return (
    <FullPageOverlay
      title={header}
      isOpen={isOpen}
      onClose={onClose}
      inlineMessageProps={inlineMessageProps}
      onSubmit={submitFormHandler}
      footer={
        <>
          <Button fullWidth={isMobile} color='tertiary' size='large' onClick={onClose}>
            Cancel
          </Button>
          <Button
            color='primary'
            fullWidth={isMobile}
            size='large'
            onClick={submitFormClickHandler}
            disabled={isShipmentUpdating || isStatusStale}>
            {isShipmentUpdating ? 'Loading...' : 'Submit'}
          </Button>
        </>
      }>
      {isDropTrailerNumberFeatureEnabled && showTrailerPrompt && (
        <TrailerNumberPrompt
          submitInOutFormHandler={submitFormHandler}
          clickHandler={onClickTrailerPrompt}
          setShowTrailerPrompt={setShowTrailerPrompt}
        />
      )}
      {isShipmentUpdating ? (
        <Spinner position='absolute' />
      ) : (
        <div>
          <div className='body-medium'>{message}</div>
          <div className='mt4'>
            <div className='text-secondary mb2'>{stopTypeNumber}</div>
            <FacilityDetails sequence={sequence} stop={currentStop} />
          </div>
          <div className='mt4'>
            <div className='text-secondary mb2'>Appointment</div>
            <div className='b body-medium'>{appointmentDateTime}</div>
          </div>
          <div className='mt4'>
            <label className='label' htmlFor='date'>
              {nextStatus === LOAD_STATE.Dispatched ? 'Pickup ETA' : `${checkType} Date`}
            </label>
            <DatePicker
              date={date}
              large
              placeholder='mm/dd/yyyy'
              onDateChange={dateChangeHandler}
            />
          </div>
          <div className='mt4 flex items-end'>
            <TimeInput
              label={nextStatus === LOAD_STATE.Dispatched ? '' : `${checkType} Time`}
              fullWidth
              value={time}
              placeholder='00:00'
              onChange={timeChangeHandler}
              helperMessage={timeZoneMessage}
              className='flex-grow-2'
              dataTest='time-input'
            />
            <span className='pl2 pb1'>{moment().tz(initialTZ).format('z')}</span>
          </div>
          <div className='mt4'>
            {shouldShowLateArrivalWarning && (
              <InlineMessage message='Late Arrival' type={INLINE_MESSAGE_VARIANTS.INFO} />
            )}
          </div>
        </div>
      )}
    </FullPageOverlay>
  )
}
